/**
 * Counting down to a moment the *server* decides.
 *
 * Every countdown in the app — the registration window on the sign-up page,
 * its preview in the Admin console — has the same problem: the deadline comes
 * from the server and the ticking happens in a browser whose clock may be
 * minutes out. A phone set five minutes fast would show the registration form
 * five minutes early and then be refused by the server, which is the worst of
 * both: the student did nothing wrong and the app told them they could.
 *
 * So the server sends its own clock with every window, and these helpers run
 * against that, offset once per fetch.
 */

import { useEffect, useState } from 'react'
import type { TimeWindow } from '@sdg/shared'

/**
 * Server time, in milliseconds, ticking once a second.
 *
 * `serverNowIso` is the clock reading that came with the payload; the offset
 * against the device is measured when it changes, which is on every refetch.
 */
export function useServerNow(serverNowIso: string | null | undefined): number {
  const [offset, setOffset] = useState(0)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!serverNowIso) {
      setOffset(0)
      return
    }
    const server = Date.parse(serverNowIso)
    if (Number.isNaN(server)) {
      setOffset(0)
      return
    }
    setOffset(server - Date.now())
  }, [serverNowIso])

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  return now + offset
}

/** Whole seconds from `nowMs` to an ISO timestamp; 0 once it has passed. */
export function secondsUntil(iso: string | null, nowMs: number): number | null {
  if (!iso) return null
  const target = Date.parse(iso)
  if (Number.isNaN(target)) return null
  return Math.max(0, Math.ceil((target - nowMs) / 1000))
}

/** 'loading' is its own state: no card should flash before the window is known. */
export type GateStatus = 'loading' | 'before' | 'open' | 'closed'

export interface WindowState {
  status: GateStatus
  /** Seconds to the next transition, or null when there is not one. */
  untilOpen: number | null
  untilClose: number | null
  /** False in development mode — the window is configured but not applied. */
  enforced: boolean
}

/**
 * Where we are in a window, re-derived once a second from the server's clock.
 *
 * Derived here rather than taken from the payload's own `status` so a
 * countdown reaching zero reveals the thing immediately, and so a page turns
 * itself over when the window closes under it — neither needs a refetch, let
 * alone a reload.
 *
 * One copy of the rule for both windows: the student registration gate and the
 * booth scanner both call this. It mirrors `windowFor` on the server without
 * replacing it — the server refuses the request independently, and if the two
 * ever disagree the server wins. That is the right way round: a clock that is
 * minutes out costs a refusal at worst, never a half-written scan.
 *
 * A *missing* window resolves to open, not closed. Turning one timed-out
 * request on venue Wi-Fi into a closed registration — or fourteen dead booth
 * scanners — would be the worse failure by far.
 */
export function useWindowState(
  window: TimeWindow | undefined,
  isPending: boolean,
): WindowState {
  const nowMs = useServerNow(window?.serverNow)

  const untilOpen = secondsUntil(window?.opensAt ?? null, nowMs)
  const untilClose = secondsUntil(window?.closesAt ?? null, nowMs)

  const status: GateStatus = isPending
    ? 'loading'
    : !window || !window.enforced
      ? 'open'
      : untilOpen !== null && untilOpen > 0
        ? 'before'
        : untilClose === 0
          ? 'closed'
          : 'open'

  return { status, untilOpen, untilClose, enforced: !!window?.enforced }
}

export interface DurationParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export function splitDuration(totalSeconds: number): DurationParts {
  const total = Math.max(0, Math.floor(totalSeconds))
  return {
    days: Math.floor(total / 86_400),
    hours: Math.floor((total % 86_400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  }
}

/**
 * A one-line duration: "3 days 4 hours", "12 minutes", "45 seconds".
 *
 * Two units at most, and the smaller one dropped when it is zero. Three weeks
 * expressed to the second reads as noise, and "0 hours 0 minutes 45 seconds"
 * is worse than "45 seconds".
 */
export function formatDuration(totalSeconds: number): string {
  const { days, hours, minutes, seconds } = splitDuration(totalSeconds)
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

  if (days > 0) return hours > 0 ? `${unit(days, 'day')} ${unit(hours, 'hour')}` : unit(days, 'day')
  if (hours > 0) {
    return minutes > 0 ? `${unit(hours, 'hour')} ${unit(minutes, 'minute')}` : unit(hours, 'hour')
  }
  if (minutes > 0) {
    return seconds > 0
      ? `${unit(minutes, 'minute')} ${unit(seconds, 'second')}`
      : unit(minutes, 'minute')
  }
  return unit(seconds, 'second')
}

/**
 * How a date and time should read on screen, in the reader's own zone.
 *
 * The event runs in Malaysia and the Committee works in MYT, but a student
 * abroad reading "08:00" has no way to know whose eight o'clock it is — so the
 * zone is named.
 */
export function formatMoment(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  })
}

// ── <input type="datetime-local"> ─────────────────────────────

/**
 * ISO instant -> the `YYYY-MM-DDTHH:mm` a datetime-local input wants, in the
 * browser's own zone.
 *
 * Built from the local parts rather than by slicing `toISOString()`, which
 * would silently shift the value by the zone offset — eight hours in Malaysia,
 * which is enough to open registration on the wrong day.
 */
export function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

/** The input's local wall-clock value -> an ISO instant, or null if empty. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
